/**
 * Pembuat slot otomatis: pilih hari, rentang jam, dan berapa kali sehari.
 *
 * Waktunya sengaja TIDAK rata. Slot yang berjarak persis sama (08:00, 10:24,
 * 12:48, …) dan berulang identik tiap hari adalah pola mesin — persis sinyal
 * yang membuat akun terlihat otomatis. Jadi:
 *
 *  - Rentang dibagi menjadi N kotak sama besar, lalu tiap slot diacak DI DALAM
 *    kotaknya sendiri. Sebarannya tetap merata sepanjang hari (tidak menumpuk),
 *    tapi jaraknya tidak pernah kelipatan yang rapi.
 *  - **Menitnya unik dalam satu hari**: tidak ada dua slot yang sama-sama :00
 *    atau sama-sama :24. Angka menit yang berulang adalah hal pertama yang
 *    terlihat janggal saat jadwal dilihat berjajar.
 *  - **Tiap hari diacak sendiri-sendiri**, sehingga sepekan tidak membentuk
 *    grid yang seragam.
 *  - Jam selesai adalah PLAFON, bukan titik tayang: "sampai 20:00" berarti slot
 *    terakhir jatuh sebelum jam 20 (mis. 19:37), tidak pernah tepat 20:00.
 */

export interface DraftSlot {
  dow: number
  minuteOfDay: number
}

/** Sumber acak; bisa disuntik agar hasilnya bisa diuji. */
export type Rng = () => number

export interface GenerateInput {
  /** 0 = Minggu … 6 = Sabtu. */
  days: number[]
  startMinute: number
  endMinute: number
  perDay: number
  rng?: Rng
}

export interface GenerateResult {
  slots: DraftSlot[]
  /** Jarak terkecil antar slot dalam satu hari — bahan peringatan pacing. */
  minGapMinutes: number | null
}

export const MAX_PER_DAY = 24

export class GenerateError extends Error {}

function acakInt(rng: Rng, minInklusif: number, maksEksklusif: number): number {
  const n = maksEksklusif - minInklusif
  if (n <= 0) return minInklusif
  return minInklusif + Math.floor(rng() * n)
}

function validasi(startMinute: number, endMinute: number, perDay: number): void {
  if (!Number.isInteger(perDay) || perDay < 1) {
    throw new GenerateError('Slots per day must be at least 1')
  }
  if (perDay > MAX_PER_DAY) {
    throw new GenerateError(`At most ${MAX_PER_DAY} slots per day`)
  }
  if (startMinute < 0 || endMinute > 1439 || startMinute > 1439 || endMinute < 0) {
    throw new GenerateError('Time must be between 00:00 and 23:59')
  }
  if (endMinute <= startMinute) {
    throw new GenerateError('End time must be after the start time')
  }
  // Tiap slot butuh kotaknya sendiri yang memuat minimal satu menit.
  if (endMinute - startMinute < perDay) {
    throw new GenerateError(
      `A ${endMinute - startMinute}-minute range is not enough for ${perDay} slots — widen the range or reduce the count`,
    )
  }
}

/**
 * Jam-jam untuk SATU hari: menaik, unik, menitnya tidak ada yang kembar, dan
 * semuanya di dalam [start, end).
 */
export function generateTimes(
  startMinute: number,
  endMinute: number,
  perDay: number,
  rng: Rng = Math.random,
): number[] {
  validasi(startMinute, endMinute, perDay)

  const lebar = (endMinute - startMinute) / perDay
  const menitTerpakai = new Set<number>()
  const out: number[] = []

  for (let i = 0; i < perDay; i++) {
    // Kotak setengah-terbuka: [awal, akhir). Kotak terakhir berhenti sebelum
    // endMinute — inilah yang membuat "sampai 20:00" tidak pernah jadi 20:00.
    const awal = Math.floor(startMinute + i * lebar)
    const akhir = Math.max(awal + 1, Math.ceil(startMinute + (i + 1) * lebar))
    const batasAtas = Math.min(akhir, endMinute)

    // Kandidat yang angka menitnya belum dipakai hari itu.
    const kandidat: number[] = []
    for (let m = awal; m < batasAtas; m++) {
      if (!menitTerpakai.has(m % 60)) kandidat.push(m)
    }

    if (kandidat.length === 0) {
      throw new GenerateError(
        'The range is too narrow to make distinct minutes — widen the range or reduce the number of slots',
      )
    }

    const pilih = kandidat[acakInt(rng, 0, kandidat.length)]
    menitTerpakai.add(pilih % 60)
    out.push(pilih)
  }

  return out
}

export function generateSlots(input: GenerateInput): GenerateResult {
  const rng = input.rng ?? Math.random
  const days = [...new Set(input.days)].sort((a, b) => a - b)
  if (days.length === 0) throw new GenerateError('Select at least one day')
  if (days.some((d) => !Number.isInteger(d) || d < 0 || d > 6)) {
    throw new GenerateError('Invalid day')
  }
  // Divalidasi sekali di muka supaya pesan errornya muncul walau hari mana pun.
  validasi(input.startMinute, input.endMinute, input.perDay)

  const slots: DraftSlot[] = []
  let minGap: number | null = null

  for (const dow of days) {
    // Diacak ulang PER HARI — kalau dipakai bersama, sepekan jadi grid seragam.
    const times = generateTimes(input.startMinute, input.endMinute, input.perDay, rng)
    for (let i = 1; i < times.length; i++) {
      const gap = times[i] - times[i - 1]
      if (minGap === null || gap < minGap) minGap = gap
    }
    for (const minuteOfDay of times) slots.push({ dow, minuteOfDay })
  }

  return { slots, minGapMinutes: minGap }
}

/**
 * Menggabungkan hasil generate ke draft yang sudah ada.
 *
 * Hari yang di-generate DIGANTI seluruhnya, hari lain dibiarkan utuh — supaya
 * "hari kerja 3× lalu akhir pekan 1×" bisa dilakukan dua kali jalan tanpa
 * saling menghapus.
 */
export function mergeGenerated(
  existing: DraftSlot[],
  generated: DraftSlot[],
  days: number[],
): DraftSlot[] {
  const diganti = new Set(days)
  const sisa = existing.filter((s) => !diganti.has(s.dow))
  const gabungan = [...sisa, ...generated]

  // Jaga-jaga: buang duplikat (dow, menit) — backend menolak payload ganda.
  const seen = new Set<string>()
  return gabungan.filter((s) => {
    const key = `${s.dow}:${s.minuteOfDay}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}
